<script lang="ts" setup>
import Footer from '@/components/layout/Footer.vue'
import Navbar from '@/components/layout/Navbar.vue'

const route = useRoute()
const { profileCheck, isProfileCompleted, updateButtonUrl, fetchProfileCompleteInfo } = useProfileCheck()
const authStore = useAuthStore()
const { isRejected, rejectedReason, isProfileVerified } = storeToRefs(authStore)
const { isAuth } = storeToRefs(authStore)
const { t } = useI18n()

const isOnCompleteProfilePage = computed(() =>
    route.path.startsWith('/complete-profile'),
)

watch(
    () => route.path,
    async () => {
        await fetchProfileCompleteInfo()
    },
    { immediate: true },
)
</script>

<template>
  <div class="layout-wrapper">
    <ClientOnly>
      <Navbar />
    </ClientOnly>
    <div class="layout-content">
      <VContainer>
        <ClientOnly>
          <VContainer
            v-if="isAuth && isRejected"
            class="mb-5 pa-0"
          >
            <VAlert
              color="error"
              variant="tonal"
              class="border"
            >
              <div class="d-flex justify-space-between align-center">
                <div class="d-flex align-center gap-x-4">
                  <VIcon
                    icon="ri-file-close-line"
                    class="text-grey-900"
                  />
                  <div class="d-flex flex-column">
                    <span class="text-lg text-grey-900 font-weight-semibold">{{ t('Your Profile Has Been Rejected') }}</span>
                    <div class="text-sm text-secondary">
                      {{ rejectedReason ? `${t('Reason')}: ${rejectedReason}` : t('Unfortunately, your profile did not meet our requirements.') }}
                    </div>
                  </div>
                </div>
                <VBtn
                  variant="outlined"
                  color="grey-900"
                  class="font-weight-semibold"
                  to="/complete-profile"
                >
                  Edit Profile
                </VBtn>
              </div>
            </VAlert>
          </VContainer>
          <VContainer
            v-else-if="
              isAuth
                && profileCheck
                && (!isProfileCompleted || !isProfileVerified)
                && !isOnCompleteProfilePage
            "
            class="mb-5 pa-0"
          >
            <VAlert
              color="warning"
              variant="tonal"
              class="border"
            >
              <div class="d-flex justify-space-between align-center">
                <template v-if="!isProfileCompleted">
                  <div class="d-flex align-center gap-x-4">
                    <VIcon
                      icon="ri-survey-line"
                      class="text-grey-900"
                    />
                    <div class="d-flex flex-column">
                      <span class="text-lg text-grey-900 font-weight-semibold">
                        {{ t("onboarding_notification.title") }}
                      </span>
                      <span
                        v-if="profileCheck && !profileCheck.step_one"
                        class="text-sm text-secondary"
                      >{{ t("onboarding_notification.step1") }}</span>
                      <span
                        v-if="profileCheck && !profileCheck.step_two"
                        class="text-sm text-secondary"
                      >{{ t("onboarding_notification.step2") }}</span>
                      <span
                        v-if="profileCheck && !profileCheck.step_three"
                        class="text-sm text-secondary"
                      >{{ t("onboarding_notification.step3") }}</span>
                      <span
                        v-if="profileCheck && !profileCheck.step_four"
                        class="text-sm text-secondary"
                      >{{ t("onboarding_notification.step4") }}</span>
                      <span
                        v-if="profileCheck && !profileCheck.step_five"
                        class="text-sm text-secondary"
                      >{{ t("onboarding_notification.step5") }}</span>
                      <span class="text-sm text-secondary">{{ t("onboarding_notification.msg") }}</span>
                    </div>
                  </div>
                  <VBtn
                    variant="outlined"
                    color="grey-900"
                    class="font-weight-semibold"
                    :to="updateButtonUrl"
                  >
                    {{ t("onboarding_notification.btn_action") }}
                  </VBtn>
                </template>
                <template v-else-if="!isProfileVerified">
                  <div class="d-flex align-center gap-x-4">
                    <VIcon
                      icon="ri-search-2-line"
                      class="text-grey-900"
                    />
                    <div class="d-flex flex-column">
                      <span class="text-lg text-grey-900 font-weight-semibold">{{
                        t('Your Profile Is Under Review')
                      }}</span>
                      <div class="text-sm text-secondary">
                        {{
                          t(
                            'Were currently reviewing your profile. Youll be notified as soon as its approved and ready for job applications.',
                          )
                        }}
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </VAlert>
          </VContainer>
        </ClientOnly>
        <main class="layout-content__main">
          <slot />
        </main>
      </VContainer>
    </div>
    <Footer />
  </div>
</template>

<style lang="scss" scoped>
.layout-content {
    margin-block: 5rem 5rem;
    flex-grow: 1;
}
.layout-wrapper {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
}
</style>
